import React, { useEffect, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { Btn, Select } from '../kit';

/**
 * Vocabulary and plumbing shared by the IP Intel Report admin tabs.
 *
 * Everything reads through the panel dispatcher (section "ip_intel", see
 * Backend/ip_intel.py GET_HANDLERS / POST_HANDLERS) except the things JSON
 * cannot carry: the Excel export, an admin download of an uploaded file, the
 * delivery upload and the sample-report cover / PDF uploads. Those go to raw
 * admin routes, whose paths live here so there is one place to change them.
 * A path the server hands back on a row (files carry download_path) always
 * wins over the default.
 */

export const S = 'ip_intel';

/** GET — ?kind=orders|leads|activity|bulk plus the tab's filters (.xlsx or .csv). */
export const EXPORT_PATH = '/api/v1/admin/ip-intel/export.xlsx';
export const EXPORT_CSV_PATH = '/api/v1/admin/ip-intel/export.csv';
/** GET — an intake or delivery file, admin-gated. */
export const FILE_PATH = (fileId: string | number) =>
  `/api/v1/admin/ip-intel/file/${encodeURIComponent(String(fileId))}`;
/** POST multipart — order_id + file (+ optional message): stores the report,
 *  marks the order delivered and e-mails the customer (ip_intel.admin_deliver). */
export const DELIVER_PATH = '/api/v1/admin/ip-intel/deliver';
/** POST multipart — field `file`: replaces a sample report's cover image or
 *  its PDF (ip_intel sample uploads; the previous file stays on disk). */
export const SAMPLE_COVER_PATH = (id: string) =>
  `/api/v1/admin/ip-intel/sample/${encodeURIComponent(id)}/cover`;
export const SAMPLE_PDF_PATH = (id: string) =>
  `/api/v1/admin/ip-intel/sample/${encodeURIComponent(id)}/pdf`;
/** Mirrors the server's sample upload limits; the server re-checks (by magic bytes). */
export const COVER_EXTS = ['png', 'jpg', 'jpeg', 'webp'];
export const COVER_MAX_MB = 5;
export const SAMPLE_PDF_MAX_MB = 50;

/** Where a bulk-pricing enquiry is sent (ip_intel TEAM_INBOX); the reply link CCs it. */
export const TEAM_INBOX = 'info@universitytech.io';

/** Mirrors ip_intel.ALLOWED_EXTENSIONS / MAX_DELIVERY_BYTES; the server re-checks. */
export const DELIVERY_EXTS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'zip'];
export const DELIVERY_MAX_MB = 100;

// ---------------------------------------------------------------------------
// Coercion — the payloads are read defensively: a list may arrive as rows,
// items or the bare array, a count as a string.
// ---------------------------------------------------------------------------
export const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const str = (v: any): string => (v === null || v === undefined ? '' : String(v));

export const list = (v: any): string[] =>
  Array.isArray(v) ? v.filter(x => x !== null && x !== undefined && x !== '').map(String)
    : (typeof v === 'string' && v.trim() ? v.split(',').map(s => s.trim()).filter(Boolean) : []);

/** The row array out of a list payload, whichever key the server used. */
export const rowsOf = (d: any, ...keys: string[]): any[] => {
  if (Array.isArray(d)) return d;
  for (const k of [...keys, 'rows', 'items', 'data']) {
    if (d && Array.isArray(d[k])) return d[k];
  }
  return [];
};

/** {total, pages} for a paged list, falling back to what arrived. */
export const pageInfo = (d: any, rows: any[], per: number) => {
  const total = num(d?.total ?? rows.length);
  const pages = num(d?.pages) || Math.max(1, Math.ceil(total / Math.max(1, per)));
  return { total, pages };
};

/** Keeps name/email searches off every keystroke. */
export function useDebounced(value: string, msDelay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), msDelay);
    return () => clearTimeout(t);
  }, [value, msDelay]);
  return v;
}

export const fmtBytes = (b: any): string => {
  const n = num(b);
  if (!n) return '';
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

export const saveBlob = (blob: Blob, name: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

const uid = () => apiService.getStoredUserId() || '';
const ymd = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');

/** Excel (or CSV) export through the admin route (audited server-side). */
export type ExportKind = 'orders' | 'leads' | 'activity' | 'bulk';

export async function downloadExport(kind: ExportKind,
                                     params: Record<string, string | number> = {},
                                     format: 'xlsx' | 'csv' = 'xlsx'): Promise<string> {
  const { blob, filename, error } = await apiService.adminFetchFile(
    uid(), format === 'csv' ? EXPORT_CSV_PATH : EXPORT_PATH, { kind, ...params });
  if (error || !blob) return error || 'Download failed';
  saveBlob(blob, filename || `ip-intel-${kind}-${ymd()}.${format}`);
  return '';
}

/** The two export buttons every list tab carries. */
export const ExportButtons: React.FC<{
  kind: ExportKind; params: Record<string, string | number>;
  onError: (e: string) => void; what: string;
}> = ({ kind, params, onError, what }) => {
  const [busy, setBusy] = useState<'' | 'xlsx' | 'csv'>('');
  const run = async (format: 'xlsx' | 'csv') => {
    setBusy(format); onError('');
    const err = await downloadExport(kind, params, format);
    setBusy('');
    if (err) onError(err);
  };
  return (
    <>
      <Btn small onClick={() => run('csv')} disabled={!!busy} title={`${what} (same filters) as CSV`}>
        {busy === 'csv' ? 'Preparing…' : 'CSV'}
      </Btn>
      <Btn small kind="primary" onClick={() => run('xlsx')} disabled={!!busy}
        title={`${what} (same filters) as an Excel workbook`}>
        {busy === 'xlsx' ? 'Preparing…' : 'Download Excel'}
      </Btn>
    </>
  );
};

/** An uploaded file (intake attachment or the delivered report). */
export async function downloadFile(file: any): Promise<string> {
  const path = str(file?.download_path || file?.admin_path || file?.admin_url)
    || (file?.id !== undefined && file?.id !== null ? FILE_PATH(file.id) : '');
  if (!path) return 'This file has no id to download it by';
  // A full URL from the server is reduced to its path: adminFetchFile adds the origin.
  const rel = path.replace(/^https?:\/\/[^/]+/i, '');
  const { blob, filename, error } = await apiService.adminFetchFile(uid(), rel);
  if (error || !blob) return error || 'Download failed';
  saveBlob(blob, filename || str(file?.filename) || 'ip-intel-file');
  return '';
}

/**
 * Multipart POST to a raw admin route, with upload progress (a delivered
 * report can be tens of MB, and a silent button for a minute reads as hung).
 * apiService has no multipart helper, so this goes through XHR with the same
 * bearer header. On a 401 it lets apiService renew the token — any cheap
 * makeRequest call does that — and tries once more.
 */
export function postMultipart(path: string, form: FormData,
                              onProgress?: (pct: number) => void, isRetry = false):
  Promise<{ data: any; error: string }> {
  return new Promise(resolve => {
    const xhr = new XMLHttpRequest();
    const url = `${apiService.getApiBaseUrl()}${path}?user_id=${encodeURIComponent(uid())}`;
    xhr.open('POST', url);
    const token = apiService.getStoredAccessToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onerror = () => resolve({ data: null, error: 'Network error — the upload did not reach the server' });
    xhr.onload = async () => {
      let body: any = null;
      try { body = JSON.parse(xhr.responseText || 'null'); } catch { body = null; }
      if (xhr.status === 401 && !isRetry && apiService.getStoredRefreshToken()) {
        await apiService.adminBillingCheck(uid());
        resolve(await postMultipart(path, form, onProgress, true));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (body && body.success === false) {
          resolve({ data: null, error: str(body.detail || body.error) || 'Request failed' });
        } else resolve({ data: body?.data ?? body, error: '' });
        return;
      }
      const d = body?.detail;
      const msg = typeof d === 'string' ? d
        : Array.isArray(d) ? d.map((x: any) => x?.msg || '').filter(Boolean).join('; ')
          : '';
      resolve({ data: null, error: msg || `Upload failed (HTTP ${xhr.status})` });
    };
    xhr.send(form);
  });
}

// ---------------------------------------------------------------------------
// Order status — the server validates every transition; this is the fallback
// map for a payload that does not carry its own `allowed_transitions`.
// ---------------------------------------------------------------------------
export type Tone = 'good' | 'warn' | 'bad' | 'neutral' | 'info';

export const STATUSES = ['created', 'paid', 'intake_submitted', 'clarification', 'in_progress',
  'delivered', 'cancelled', 'refunded'] as const;

export const STATUS_META: Record<string, { label: string; tone: Tone; hint: string }> = {
  created: { label: 'Awaiting payment', tone: 'neutral', hint: 'Razorpay order created, not paid' },
  paid: { label: 'Paid · awaiting intake', tone: 'info', hint: 'Paid; the customer has not sent the technology details yet' },
  intake_submitted: { label: 'Intake submitted', tone: 'warn', hint: 'Details in — the 7-day clock is running; start work' },
  clarification: { label: 'Clarification requested', tone: 'warn', hint: 'Waiting on the customer; the clock is paused' },
  in_progress: { label: 'In progress', tone: 'info', hint: 'Being written' },
  delivered: { label: 'Delivered', tone: 'good', hint: 'Report sent' },
  cancelled: { label: 'Cancelled', tone: 'neutral', hint: 'Cancelled' },
  refunded: { label: 'Refunded', tone: 'bad', hint: 'Payment refunded' },
};

export const statusLabel = (s: any) => STATUS_META[str(s)]?.label || str(s) || '—';

// Mirrors ip_intel.TRANSITIONS. No paid -> in_progress: in_progress closes the
// intake form, so work starts only once the customer's intake is in.
export const DEFAULT_TRANSITIONS: Record<string, string[]> = {
  created: ['cancelled'],
  paid: ['clarification', 'cancelled', 'refunded'],
  intake_submitted: ['in_progress', 'clarification', 'cancelled', 'refunded'],
  clarification: ['in_progress', 'cancelled', 'refunded'],
  in_progress: ['clarification', 'cancelled', 'refunded'],
  delivered: ['in_progress', 'refunded'],
  cancelled: ['refunded'],
  refunded: [],
};

/** Statuses from which a report can be uploaded and delivered. */
export const DELIVERABLE = new Set(['intake_submitted', 'in_progress', 'clarification', 'delivered']);

const PILL: Record<Tone, string> = {
  good: 'text-green-700 bg-green-50 border-green-200',
  warn: 'text-amber-700 bg-amber-50 border-amber-200',
  bad: 'text-red-600 bg-red-50 border-red-200',
  neutral: 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300',
  info: 'text-secondary-500 bg-secondary-100 border-secondary-400',
};

export const Pill: React.FC<{ tone: Tone; children: React.ReactNode; title?: string }> =
  ({ tone, children, title }) => (
  <span title={title}
    className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${PILL[tone]}`}>{children}</span>
);

export const StatusPill: React.FC<{ status: any }> = ({ status }) => {
  const m = STATUS_META[str(status)];
  return <Pill tone={m?.tone || 'neutral'} title={m?.hint}>{m?.label || str(status) || '—'}</Pill>;
};

/** Days between two instants, one decimal; '' when either is missing. */
export const daysBetween = (a: any, b: any): string => {
  const t0 = Date.parse(str(a)), t1 = Date.parse(str(b));
  if (!Number.isFinite(t0) || !Number.isFinite(t1)) return '';
  return (Math.round(((t1 - t0) / 86400000) * 10) / 10).toString();
};

/**
 * When the report is due: the server's own due_at if it sends one (it knows
 * how long clarifications paused the clock), else intake + turnaround.
 */
export const dueAt = (o: any, turnaroundDays = 7): Date | null => {
  if (o?.due_at) {
    const d = new Date(o.due_at);
    return isNaN(d.getTime()) ? null : d;
  }
  const t = Date.parse(str(o?.intake_submitted_at));
  if (!Number.isFinite(t)) return null;
  return new Date(t + turnaroundDays * 86400000);
};

export const isOverdue = (o: any, turnaroundDays = 7): boolean => {
  // The server's flag counts clarification pauses; trust it when present.
  if (typeof o?.overdue === 'boolean') return o.overdue;
  const s = str(o?.status);
  if (!['intake_submitted', 'in_progress'].includes(s)) return false;
  const d = dueAt(o, turnaroundDays);
  return !!d && d.getTime() < Date.now();
};

/** A small labelled fact box, used across the drawer and the pricing preview. */
export const Fact: React.FC<{ label: string; value: React.ReactNode; title?: string }> =
  ({ label, value, title }) => (
  <div className="rounded-lg border border-grayscale-300 px-3 py-2 min-w-0" title={title}>
    <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">{label}</div>
    <div className="text-sm text-grayscale-1300 font-medium break-words">
      {value === null || value === undefined || value === '' ? '—' : value}
    </div>
  </div>
);

/** Prev/next paging footer — the same shape FunnelPanel uses. */
export const Pager: React.FC<{
  page: number; pages: number; total: number; shown: number; per: number;
  onPage: (p: number) => void; perPage: string; onPerPage: (v: string) => void;
}> = ({ page, pages, total, shown, per, onPage, perPage, onPerPage }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
    <span>
      Page {page + 1} of {pages} · showing {shown ? page * per + 1 : 0}–{page * per + shown} of {total.toLocaleString()}
    </span>
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={perPage} onChange={onPerPage} options={['25', '50', '100', '200']}
        labels={{ '25': '25 / page', '50': '50 / page', '100': '100 / page', '200': '200 / page' }} />
      <Btn small onClick={() => onPage(0)} disabled={page === 0}>« First</Btn>
      <Btn small onClick={() => onPage(Math.max(0, page - 1))} disabled={page === 0}>‹ Prev</Btn>
      <Btn small onClick={() => onPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1}>Next ›</Btn>
      <Btn small onClick={() => onPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
    </div>
  </div>
);

/** Copy-to-clipboard chip for ids (Razorpay order / payment ids, refs). */
export const CopyId: React.FC<{ value: any }> = ({ value }) => {
  const [done, setDone] = useState(false);
  const v = str(value);
  if (!v) return <span className="text-grayscale-1100">—</span>;
  const copy = async () => {
    try { await navigator.clipboard.writeText(v); setDone(true); setTimeout(() => setDone(false), 1500); }
    catch { window.prompt('Copy this:', v); }
  };
  return (
    <span className="inline-flex items-start gap-1 max-w-full rounded-md border border-grayscale-300 bg-grayscale-200
      pl-2 pr-1 py-0.5 text-xs font-mono text-grayscale-1300">
      <span className="break-all min-w-0">{v}</span>
      <button type="button" onClick={copy} title="Copy"
        className="shrink-0 whitespace-nowrap rounded px-1 text-[10px] font-sans text-grayscale-1100 hover:bg-white">
        {done ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
};
